import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiService, LoginRequest, LoginResponse, ApiResponse } from '../services/api';

interface User {
  _id: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  gstNumber: string;
  panNumber: string;
  panImage: string;
  profileImageUrl: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
  role: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginRequest) => Promise<ApiResponse<LoginResponse>>;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
  error: string | null;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Check if user is already authenticated on app load
    const token = apiService.getToken();
    if (token) {
      // In a real app, you might want to validate the token with the server
      // For now, we'll assume the token is valid if it exists
      const userData = localStorage.getItem('userData');
      if (userData) {
        try {
          setUser(JSON.parse(userData));
        } catch (error) {
          console.error('Error parsing user data:', error);
          apiService.logout();
        }
      }
    }
    setIsLoading(false);

    // Listen for unauthorized events (401 errors)
    const handleUnauthorized = () => {
      console.warn('Unauthorized event received - logging out user');
      setUser(null);
      setError(null);
      apiService.logout();
      localStorage.removeItem('userData');
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);

    // Cleanup listener on unmount
    return () => {
      window.removeEventListener('auth:unauthorized', handleUnauthorized);
    };
  }, []);

  const login = async (credentials: LoginRequest): Promise<ApiResponse<LoginResponse>> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiService.login(credentials);
      
      console.log('AuthContext - Full response:', response);
      
      if (response.success && response.data) {
        // The response.data contains the actual API response
        const apiResponse = response.data as any;
        console.log('AuthContext - API Response:', apiResponse);
        
        if (apiResponse.success && apiResponse.data && apiResponse.data.vendor) {
          console.log('AuthContext - Vendor data from API:', apiResponse.data.vendor);
          console.log('AuthContext - Vendor phone:', apiResponse.data.vendor.phone);
          console.log('AuthContext - Vendor address:', apiResponse.data.vendor.address);
          console.log('AuthContext - Vendor city:', apiResponse.data.vendor.city);
          console.log('AuthContext - Vendor state:', apiResponse.data.vendor.state);
          console.log('AuthContext - Vendor zip:', apiResponse.data.vendor.zip);
          console.log('AuthContext - Vendor gstNumber:', apiResponse.data.vendor.gstNumber);
          console.log('AuthContext - Vendor panNumber:', apiResponse.data.vendor.panNumber);
          console.log('AuthContext - Vendor panImage:', apiResponse.data.vendor.panImage);
          console.log('AuthContext - Vendor createdAt:', apiResponse.data.vendor.createdAt);
          
          // Map the vendor data to user format
          const userData: User = {
            _id: apiResponse.data.vendor._id,
            email: apiResponse.data.vendor.email,
            name: apiResponse.data.vendor.name,
            phone: apiResponse.data.vendor.phone,
            address: apiResponse.data.vendor.address,
            city: apiResponse.data.vendor.city,
            state: apiResponse.data.vendor.state,
            zip: apiResponse.data.vendor.zip,
            gstNumber: apiResponse.data.vendor.gstNumber,
            panNumber: apiResponse.data.vendor.panNumber,
            panImage: apiResponse.data.vendor.panImage,
            profileImageUrl: apiResponse.data.vendor.profileImageUrl || '',
            createdAt: apiResponse.data.vendor.createdAt,
            updatedAt: apiResponse.data.vendor.updatedAt,
            __v: apiResponse.data.vendor.__v,
            role: 'vendor'
          };
          console.log('AuthContext - Mapped User data:', userData);
          setUser(userData);
          localStorage.setItem('userData', JSON.stringify(userData));
          return response;
        } else {
          console.log('AuthContext - Invalid response format:', apiResponse);
          setError('Invalid response format from server');
          return response;
        }
      } else {
        console.log('AuthContext - Response not successful:', response);
        setError(response.error || 'Login failed');
        return response;
      }
    } catch (error) {
      const errorMessage = 'An unexpected error occurred during login';
      setError(errorMessage);
      return {
        success: false,
        error: errorMessage,
      };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    setError(null);
    apiService.logout();
    localStorage.removeItem('userData');
  };

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      console.log('AuthContext - Current user:', user);
      console.log('AuthContext - Updating with data:', userData);
      const updatedUser = { ...user, ...userData };
      console.log('AuthContext - Updated user:', updatedUser);
      setUser(updatedUser);
      localStorage.setItem('userData', JSON.stringify(updatedUser));
    }
  };

  const clearError = () => {
    setError(null);
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    updateUser,
    error,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
